// src/pages/SuspiciousTransactionAlerts.jsx
import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  fetchAlertTransactionsThunk,
  fetchAlertFilterOptionsThunk,
  fetchAllAlertTransactions,
} from '../features/transactions/transactionThunks';
import { exportDataToExcel } from '../exportDataToExcel';
import Sidebar from '../Component/Sidebar';
import '../CSS/TransactionAlertsTable.css'; // Reusing the CSS
import ClipThumbnail from '../Component/ClipThumbnail';
import { clipThumbnailUrl } from '../utils/clipThumbnail';

/* -------------------------------------------------------- */
/* Filter model — the URL is the source of truth, so filters */
/* survive refresh and "Back" from a transaction's page.      */
/* -------------------------------------------------------- */
const DEFAULT_FILTERS = {
  store: '',
  device: '',
  status: 'flagged',   // flagged | unflagged | all
  review: 'all',       // all | verified | unverified
  video: 'all',        // all | with | without
  dateFrom: '',
  dateTo: '',
  search: '',
  ordering: '-date',   // -date | date
};

const STATUS_OPTIONS = [
  { value: 'flagged', label: 'Flagged' },
  { value: 'unflagged', label: 'Not flagged' },
  { value: 'all', label: 'All' },
];
const REVIEW_OPTIONS = [
  { value: 'all', label: 'Any review status' },
  { value: 'unverified', label: 'Not verified yet' },
  { value: 'verified', label: 'Verified' },
];
const VIDEO_OPTIONS = [
  { value: 'all', label: 'With or without video' },
  { value: 'with', label: 'Has video' },
  { value: 'without', label: 'No video' },
];
const ORDERING_OPTIONS = [
  { value: '-date', label: 'Newest first' },
  { value: 'date', label: 'Oldest first' },
];

const readFilters = (searchParams) => {
  const f = { ...DEFAULT_FILTERS };
  Object.keys(DEFAULT_FILTERS).forEach((key) => {
    const v = searchParams.get(key);
    if (v !== null) f[key] = v;
  });
  return f;
};

const labelFor = (options, value) => options.find((o) => o.value === value)?.label || value;

const formatDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleString();
};

const formatShortDate = (ymd) =>
  new Date(`${ymd}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

const SuspiciousTransactionAlerts = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(() => readFilters(searchParams), [searchParams]);
  const filtersKey = JSON.stringify(filters);

  const [searchInput, setSearchInput] = useState(filters.search);
  const [showFilters, setShowFilters] = useState(false);
  const [videoOverlay, setVideoOverlay] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);

  const listRequestRef = useRef(null);
  const optionsRequestRef = useRef(null);
  const sentinelRef = useRef(null);
  const pushedSearchRef = useRef(filters.search);

  const {
    items: transactions = [],
    count = 0,
    next = null,
    loading = false,
    loadingMore = false,
    error = null,
  } = useSelector((state) => state.transactions?.alerts || {});

  const {
    stores = [],
    devices = [],
    loading: optionsLoading = false,
    error: optionsError = null,
  } = useSelector((state) => state.transactions?.alertFilterOptions || {});

  /* -------------------------------------------------------- */
  /* Updating filters                                          */
  /* -------------------------------------------------------- */
  const updateFilters = useCallback((changes) => {
    setSearchParams((prev) => {
      const nextParams = new URLSearchParams(prev);
      Object.entries(changes).forEach(([key, value]) => {
        if (value === '' || value === null || value === undefined || value === DEFAULT_FILTERS[key]) {
          nextParams.delete(key);
        } else {
          nextParams.set(key, value);
        }
      });
      return nextParams;
    }, { replace: true });
  }, [setSearchParams]);

  const handleStoreChange = (store) => {
    // Devices differ per store, so a device picked for another store would match nothing
    updateFilters({ store, device: '' });
  };

  const clearAllFilters = () => {
    setSearchInput('');
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  // Debounce search typing → URL (→ backend)
  useEffect(() => {
    if (searchInput === filters.search) return undefined;
    const t = setTimeout(() => {
      pushedSearchRef.current = searchInput;
      updateFilters({ search: searchInput });
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput, filters.search, updateFilters]);

  // Keep the input in sync when the URL changes from elsewhere (Reset, chip ×,
  // back button) — but not for our own debounced push, or we'd eat keystrokes
  useEffect(() => {
    if (filters.search !== pushedSearchRef.current) {
      pushedSearchRef.current = filters.search;
      setSearchInput(filters.search);
    }
  }, [filters.search]);

  /* -------------------------------------------------------- */
  /* Data loading                                              */
  /* -------------------------------------------------------- */
  const loadPage = useCallback((offset) => {
    if (listRequestRef.current) listRequestRef.current.abort();
    listRequestRef.current = dispatch(fetchAlertTransactionsThunk({ filters, offset }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, filtersKey]);

  // Fresh load whenever any filter changes
  useEffect(() => {
    loadPage(0);
  }, [loadPage]);

  // Dropdown data (stores always; devices narrowed to the chosen store)
  useEffect(() => {
    if (optionsRequestRef.current) optionsRequestRef.current.abort();
    optionsRequestRef.current = dispatch(fetchAlertFilterOptionsThunk({ store: filters.store }));
  }, [dispatch, filters.store]);

  // Abort in-flight requests when leaving the page
  useEffect(() => () => {
    listRequestRef.current?.abort();
    optionsRequestRef.current?.abort();
  }, []);

  /* -------------------------------------------------------- */
  /* Infinite scroll                                           */
  /* -------------------------------------------------------- */
  const loadMore = useCallback(() => {
    if (next && !loading && !loadingMore) loadPage(transactions.length);
  }, [next, loading, loadingMore, loadPage, transactions.length]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) loadMore(); },
      { root: null, rootMargin: '200px', threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore]);

  /* -------------------------------------------------------- */
  /* Export                                                    */
  /* -------------------------------------------------------- */
  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const { rows, total } = await fetchAllAlertTransactions(filters);
      if (rows.length === 0) {
        setExportError('Nothing to export. No transactions match the current filters.');
        return;
      }
      const sheet = rows.map((t) => ({
        'Transaction ID': t.transaction_id || t.id,
        Store: t.store?.store_name || '',
        City: t.store?.store_city || '',
        Description: t.description || '',
        Device: t.device || '',
        Date: formatDate(t.date),
        Flagged: t.flagged ? 'Yes' : 'No',
        Verified: t.verified ? 'Yes' : 'No',
        'Video URL': t.video_evidence || '',
      }));
      const storeName = stores.find((s) => String(s.id) === filters.store)?.store_name;
      const stamp = new Date().toISOString().slice(0, 10);
      const name = ['transactions', storeName, stamp].filter(Boolean).join('-').replace(/[^\w-]+/g, '_');
      exportDataToExcel(sheet, name);
      if (total > rows.length) {
        setExportError(`Exported the first ${rows.length} of ${total} rows. Narrow the filters to export the rest.`);
      }
    } catch (e) {
      setExportError('Export failed. Check your connection and try again.');
    } finally {
      setExporting(false);
    }
  };

  /* -------------------------------------------------------- */
  /* Derived UI state                                          */
  /* -------------------------------------------------------- */
  const selectedStore = stores.find((s) => String(s.id) === filters.store);

  const activeChips = [];
  if (filters.store) {
    activeChips.push({ key: 'store', label: `Store: ${selectedStore?.store_name || `#${filters.store}`}`, clear: () => handleStoreChange('') });
  }
  if (filters.status !== DEFAULT_FILTERS.status) {
    activeChips.push({ key: 'status', label: `Status: ${labelFor(STATUS_OPTIONS, filters.status)}`, clear: () => updateFilters({ status: DEFAULT_FILTERS.status }) });
  }
  if (filters.review !== 'all') {
    activeChips.push({ key: 'review', label: labelFor(REVIEW_OPTIONS, filters.review), clear: () => updateFilters({ review: 'all' }) });
  }
  if (filters.video !== 'all') {
    activeChips.push({ key: 'video', label: labelFor(VIDEO_OPTIONS, filters.video), clear: () => updateFilters({ video: 'all' }) });
  }
  if (filters.device) {
    activeChips.push({ key: 'device', label: `Device: ${filters.device}`, clear: () => updateFilters({ device: '' }) });
  }
  if (filters.dateFrom) {
    activeChips.push({ key: 'dateFrom', label: `From ${formatShortDate(filters.dateFrom)}`, clear: () => updateFilters({ dateFrom: '' }) });
  }
  if (filters.dateTo) {
    activeChips.push({ key: 'dateTo', label: `To ${formatShortDate(filters.dateTo)}`, clear: () => updateFilters({ dateTo: '' }) });
  }
  if (filters.search) {
    activeChips.push({ key: 'search', label: `Search: "${filters.search}"`, clear: () => { setSearchInput(''); updateFilters({ search: '' }); } });
  }
  if (filters.ordering !== DEFAULT_FILTERS.ordering) {
    activeChips.push({ key: 'ordering', label: labelFor(ORDERING_OPTIONS, filters.ordering), clear: () => updateFilters({ ordering: DEFAULT_FILTERS.ordering }) });
  }
  // Panel-only filters (store & search live in the toolbar)
  const panelFilterCount = activeChips.filter((c) => !['store', 'search'].includes(c.key)).length;

  const dateRangeInvalid = filters.dateFrom && filters.dateTo && filters.dateFrom > filters.dateTo;
  const hasNonDefaultFilters = activeChips.length > 0;
  const storeCountKey = filters.status === 'flagged' ? 'flagged_count' : 'transaction_count';

  /* -------------------------------------------------------- */
  /* Small components                                          */
  /* -------------------------------------------------------- */
  const handleRowClick = (id) => navigate(`/transaction/${id}`);
  const handleVideoClick = (e, videoUrl) => {
    e.stopPropagation();
    setVideoOverlay(videoUrl);
  };
  const closeVideoOverlay = () => setVideoOverlay(null);

  const VideoThumbnail = ({ transaction }) => {
    if (!transaction.video_evidence) {
      return (
        <div className="ta-no-video">
          <div className="ta-no-video-content">
            <div className="ta-no-video-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8 0-1.85.63-3.55 1.69-4.9L16.9 18.31C15.55 19.37 13.85 20 12 20zm6.31-3.1L7.1 5.69C8.45 4.63 10.15 4 12 4c4.42 0 8 3.58 8 8 0 1.85-.63 3.55-1.69 4.9z" />
              </svg>
            </div>
            <span className="ta-no-video-text">No Media</span>
          </div>
        </div>
      );
    }
    return (
      <div
        className="ta-video-thumbnail"
        onClick={(e) => handleVideoClick(e, transaction.video_evidence)}
        role="button"
        aria-label={`Play video for transaction ${transaction.transaction_id || transaction.id}`}
      >
        <ClipThumbnail videoUrl={transaction.video_evidence} />
        <div className="ta-play-button">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      </div>
    );
  };

  const SkeletonRows = () => (
    <>
      {[0, 1, 2, 3, 4].map((i) => (
        <tr key={i} className="ta-skeleton-row">
          <td><div className="ta-skeleton-pulse" /></td>
          <td><div className="ta-skeleton-pulse" /><div className="ta-skeleton-pulse ta-skeleton-short" /></td>
          <td><div className="ta-skeleton-pulse" /></td>
          <td><div className="ta-skeleton-pulse" /></td>
          <td><div className="ta-skeleton-pulse ta-skeleton-center" /></td>
        </tr>
      ))}
    </>
  );

  const showSkeleton = loading && transactions.length === 0;
  const showEmpty = !loading && !error && transactions.length === 0;

  /* -------------------------------------------------------- */
  /* Render                                                   */
  /* -------------------------------------------------------- */
  return (
    <div className="index-box">
      <Sidebar />
      <div className="sta-suspicious-alerts-container">
        {/* Header Banner */}
        <div className="sta-header-banner" style={{ backgroundImage: "url('/banner/transaction.jpg')" }}>
          <div className="sta-header-banner-overlay" />
          <div className="sta-header-banner-content">
            <h1>Suspicious Transaction Alerts</h1>
          </div>
        </div>

        {/* Toolbar */}
        <div className="sta-toolbar">
          <div className="sta-toolbar-title">
            <svg className="sta-toolbar-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span>Transactions</span>
            <span className="sta-result-count" aria-live="polite">
              {loading && transactions.length === 0 ? 'Loading…' : `${count.toLocaleString()} found`}
            </span>
          </div>

          <div className="sta-toolbar-spacer" />

          <label className="sta-store-select-wrap">
            <span className="sta-visually-hidden">Store</span>
            <select
              className="sta-select sta-store-select"
              value={filters.store}
              onChange={(e) => handleStoreChange(e.target.value)}
              disabled={optionsLoading && stores.length === 0}
            >
              <option value="">
                {optionsLoading && stores.length === 0 ? 'Loading stores…' : 'All stores'}
              </option>
              {stores.map((s) => (
                <option key={s.id} value={String(s.id)}>
                  {s.store_name}{s.store_city ? `, ${s.store_city}` : ''} ({s[storeCountKey] ?? 0}){s.is_active === false ? ' – inactive' : ''}
                </option>
              ))}
            </select>
          </label>

          <div className="sta-search-container">
            <input
              type="search"
              placeholder="Search transactions"
              title="Searches transaction ID, description, device, store name and city"
              aria-label="Search transactions"
              className="sta-search-input"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  pushedSearchRef.current = searchInput;
                  updateFilters({ search: searchInput });
                }
              }}
            />
            <span className="sta-search-btn" aria-hidden="true">
              <svg className="sta-search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </span>
          </div>

          <button
            className={`sta-filters-btn ${showFilters ? 'sta-btn-active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
            aria-expanded={showFilters}
          >
            <span className="sta-filters-icon">≡</span> Filters
            {panelFilterCount > 0 && <span className="sta-filter-badge">{panelFilterCount}</span>}
          </button>

          <button className="sta-export-btn" onClick={handleExport} disabled={exporting || count === 0}>
            <span className="sta-export-icon">⬇</span> {exporting ? 'Exporting…' : 'Export to Excel'}
          </button>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="sta-filters-panel">
            <div className="sta-filter-grid">
              <div className="sta-field sta-field-wide">
                <span className="sta-field-label">Status</span>
                <div className="sta-segmented" role="radiogroup" aria-label="Status">
                  {STATUS_OPTIONS.map((o) => (
                    <button
                      key={o.value}
                      role="radio"
                      aria-checked={filters.status === o.value}
                      className={`sta-segment ${filters.status === o.value ? 'sta-segment-on' : ''}`}
                      onClick={() => updateFilters({ status: o.value })}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>

              <label className="sta-field">
                <span className="sta-field-label">Review</span>
                <select className="sta-select" value={filters.review} onChange={(e) => updateFilters({ review: e.target.value })}>
                  {REVIEW_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </label>

              <label className="sta-field">
                <span className="sta-field-label">Video evidence</span>
                <select className="sta-select" value={filters.video} onChange={(e) => updateFilters({ video: e.target.value })}>
                  {VIDEO_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </label>

              <label className="sta-field">
                <span className="sta-field-label">Device</span>
                <select
                  className="sta-select"
                  value={filters.device}
                  onChange={(e) => updateFilters({ device: e.target.value })}
                  disabled={devices.length === 0 && !filters.device}
                >
                  <option value="">
                    {devices.length === 0 ? (optionsLoading ? 'Loading devices…' : 'No devices recorded') : 'All devices'}
                  </option>
                  {filters.device && !devices.includes(filters.device) && (
                    <option value={filters.device}>{filters.device}</option>
                  )}
                  {devices.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </label>

              <label className="sta-field">
                <span className="sta-field-label">From</span>
                <input
                  type="date"
                  className="sta-select"
                  value={filters.dateFrom}
                  max={filters.dateTo || undefined}
                  onChange={(e) => updateFilters({ dateFrom: e.target.value })}
                />
              </label>

              <label className="sta-field">
                <span className="sta-field-label">To</span>
                <input
                  type="date"
                  className="sta-select"
                  value={filters.dateTo}
                  min={filters.dateFrom || undefined}
                  onChange={(e) => updateFilters({ dateTo: e.target.value })}
                />
              </label>

              <label className="sta-field">
                <span className="sta-field-label">Sort</span>
                <select className="sta-select" value={filters.ordering} onChange={(e) => updateFilters({ ordering: e.target.value })}>
                  {ORDERING_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </label>
            </div>

            {dateRangeInvalid && (
              <p className="sta-inline-warning">The "From" date is after the "To" date, so nothing can match.</p>
            )}
            {optionsError && <p className="sta-inline-warning">{optionsError}</p>}
          </div>
        )}

        {/* Active filters */}
        {hasNonDefaultFilters && (
          <div className="sta-active-filters">
            {activeChips.map((chip) => (
              <button key={chip.key} className="sta-chip" onClick={chip.clear} aria-label={`Remove filter ${chip.label}`}>
                {chip.label}
                <span className="sta-chip-x" aria-hidden="true">×</span>
              </button>
            ))}
            <button className="sta-clear-all" onClick={clearAllFilters}>Reset filters</button>
          </div>
        )}

        {exportError && (
          <div className="sta-notice" role="status">
            <span>{exportError}</span>
            <button className="sta-notice-close" onClick={() => setExportError(null)} aria-label="Dismiss">×</button>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="ta-error-container">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
            </svg>
            <p>{error}</p>
            <button className="sta-retry-btn" onClick={() => loadPage(transactions.length > 0 ? transactions.length : 0)}>Try again</button>
          </div>
        )}

        {/* Data table */}
        <div className={`ta-card-content ${loading && transactions.length > 0 ? 'sta-refreshing' : ''}`}>
          {showEmpty ? (
            <div className="ta-empty-state">
              <div className="ta-empty-icon">📊</div>
              {hasNonDefaultFilters ? (
                <>
                  <h3>No transactions match these filters</h3>
                  <p>Remove a filter above or reset them all.</p>
                  <button className="sta-retry-btn" onClick={clearAllFilters}>Reset filters</button>
                </>
              ) : (
                <>
                  <h3>No flagged transactions</h3>
                  <p>Flagged transactions will appear here as soon as they're reported.</p>
                </>
              )}
            </div>
          ) : (
            <table className="ta-transactions-table">
              <thead>
                <tr>
                  <th className="ta-store-column">Store</th>
                  <th className="ta-transaction-column">Transaction</th>
                  <th className="ta-date-column">Date</th>
                  <th className="ta-device-column">Device</th>
                  <th className="ta-video-column">Video</th>
                </tr>
              </thead>
              <tbody>
                {showSkeleton ? <SkeletonRows /> : transactions.map((transaction) => (
                  <tr
                    key={transaction.id}
                    className={`ta-table-row ${transaction.flagged ? 'ta-flagged-row' : ''}`}
                    onClick={() => handleRowClick(transaction.id)}
                  >
                    <td className="ta-store-cell">
                      <div className="ta-store-info">
                        {transaction.store?.image ? (
                          <img
                            src={transaction.store.image}
                            alt={transaction.store?.store_name || 'Store'}
                            className="ta-store-image"
                          />
                        ) : (
                          <div className="ta-store-image-placeholder">
                            {(transaction.store?.store_name || 'S').charAt(0)}
                          </div>
                        )}
                        <div className="ta-store-details">
                          <div className="ta-store-name">{transaction.store?.store_name || '—'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="ta-transaction-cell">
                      <div className="ta-transaction-header">
                        <span className="ta-transaction-id">{transaction.transaction_id || transaction.id}</span>
                        {transaction.flagged && (
                          <span className="ta-status-badge"><span className="ta-status-dot" />Alert</span>
                        )}
                        {transaction.verified && <span className="ta-verified-badge">Verified</span>}
                      </div>
                      <div className="ta-transaction-description" title={transaction.description || ''}>
                        {transaction.description || '—'}
                      </div>
                    </td>
                    <td className="ta-date-cell">{formatDate(transaction.date)}</td>
                    <td className="ta-device-cell">
                      <div className="ta-device-info">
                        <span className="ta-device-name">{transaction.device || '—'}</span>
                      </div>
                    </td>
                    <td className="ta-video-cell">
                      <div className="ta-video-container">
                        <VideoThumbnail transaction={transaction} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Infinite scroll sentinel */}
          {next && !error && (
            <div ref={sentinelRef} className="ta-loading-indicator">
              {loadingMore ? (
                <div className="ta-loading-spinner">
                  <div className="ta-spinner" />
                  <span>Loading more…</span>
                </div>
              ) : (
                <div className="ta-pagination-hint">
                  <span>Showing {transactions.length} of {count}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Video Overlay */}
        {videoOverlay && (
          <div className="ta-video-overlay" onClick={closeVideoOverlay}>
            <div className="ta-video-modal" onClick={(e) => e.stopPropagation()}>
              <button className="ta-close-button" onClick={closeVideoOverlay} aria-label="Close video">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
              <video className="ta-video-player" src={videoOverlay} poster={clipThumbnailUrl(videoOverlay) || undefined} controls autoPlay />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuspiciousTransactionAlerts;